/**
 * MyOnboardingSection — Employee self-service onboarding view.
 *
 * Displayed in the Portal page for employees with active onboarding.
 * Shows a timeline of tasks grouped by category (Pre-Start → Day 1 → Week 1 → Month 1),
 * with inline document upload, form completion, and content viewing.
 */
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Stack, Chip, LinearProgress,
  Button, Alert, Stepper, Step, StepLabel, StepContent,
  ListItem, ListItemIcon, ListItemText,
  TextField, Collapse,
} from '@mui/material';
import {
  CheckCircle as DoneIcon, RadioButtonUnchecked as PendingIcon,
  Warning as OverdueIcon,
  Upload as UploadIcon, OpenInNew as LinkIcon,
  Description as DocIcon, Assignment as FormIcon,
  PlayCircle as ContentIcon, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import {
  onboardingApi,
  OnboardingTask,
} from '@/api/endpoints/hr';
import { useNotificationStore } from '@/stores/notificationStore';
import { formatDate } from '@/utils/format';
import { TYPE } from '@/theme/typography';

const CATEGORIES = [
  { key: 'pre_start', label: 'Pre-Start', description: 'Before your first day' },
  { key: 'day_one', label: 'Day 1', description: 'Your first day' },
  { key: 'week_one', label: 'Week 1', description: 'Getting settled' },
  { key: 'month_one', label: 'Month 1', description: 'First month milestones' },
] as const;

const TASK_TYPE_ICONS: Record<string, React.ReactNode> = {
  document: <DocIcon fontSize="small" />,
  form: <FormIcon fontSize="small" />,
  content: <ContentIcon fontSize="small" />,
  checklist: <PendingIcon fontSize="small" />,
  approval: <PendingIcon fontSize="small" />,
};

function TaskCard({
  task,
  onComplete,
  onUpload,
}: {
  task: OnboardingTask;
  onComplete: (taskId: string, formData?: Record<string, unknown>) => void;
  onUpload: (taskId: string, file: File) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const isDone = task.status === 'completed' || task.status === 'skipped';

  const statusIcon = isDone ? (
    <DoneIcon color="success" />
  ) : task.status === 'overdue' || (task.due_date && new Date(task.due_date) < new Date()) ? (
    <OverdueIcon color="error" />
  ) : (
    TASK_TYPE_ICONS[task.task_type] || <PendingIcon color="disabled" />
  );

  const hasDetails = task.description || task.task_type === 'document' || task.task_type === 'form' || task.content_url;

  return (
    <Card variant="outlined" sx={{ mb: 1, opacity: isDone ? 0.7 : 1 }}>
      <ListItem
        sx={{ cursor: hasDetails ? 'pointer' : 'default' }}
        onClick={() => hasDetails && setExpanded(!expanded)}
        secondaryAction={
          <Stack direction="row" alignItems="center" gap={0.5}>
            {task.is_required && <Chip label="Required" size="small" color="error" variant="outlined" sx={{ fontSize: TYPE.xs, height: 18 }} />}
            <Chip label={task.assignee_type} size="small" variant="outlined" sx={{ fontSize: TYPE.xs, height: 18 }} />
            {task.due_date && (
              <Typography variant="caption" color={new Date(task.due_date) < new Date() && !isDone ? 'error' : 'text.secondary'}>
                {task.due_date}
              </Typography>
            )}
            {hasDetails && (expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
          </Stack>
        }
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{statusIcon}</ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={TYPE.medium}
              sx={{ textDecoration: isDone ? 'line-through' : 'none' }}>
              {task.title}
            </Typography>
          }
        />
      </ListItem>

      {hasDetails && (
        <Collapse in={expanded}>
          <Box sx={{ px: 3, pb: 2 }}>
            {task.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {task.description}
              </Typography>
            )}

            {/* Document upload */}
            {task.task_type === 'document' && !isDone && (
              <Box sx={{ mb: 1 }}>
                {task.document_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Required: {task.document_name}
                  </Typography>
                )}
                {task.documents.map(doc => (
                  <Chip key={doc.id} label={`${doc.file_name} (${doc.status})`} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                    color={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'error' : 'default'}
                  />
                ))}
                <input type="file" hidden ref={fileRef}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onUpload(task.id, f);
                  }}
                />
                <Button size="small" startIcon={<UploadIcon />} variant="outlined"
                  onClick={() => fileRef.current?.click()} sx={{ mt: 0.5 }}>
                  Upload Document
                </Button>
              </Box>
            )}

            {/* Form fields */}
            {task.task_type === 'form' && !isDone && task.form_fields && (
              <Box sx={{ mb: 1 }}>
                {((task.form_fields as any)?.fields || []).map((field: any) => (
                  <TextField key={field.name} label={field.label} size="small" fullWidth
                    required={field.required} sx={{ mb: 1 }}
                    value={formValues[field.name] || ''}
                    onChange={(e) => setFormValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                  />
                ))}
              </Box>
            )}

            {/* Content link */}
            {task.task_type === 'content' && task.content_url && (
              <Button size="small" startIcon={<LinkIcon />} variant="outlined"
                href={task.content_url} target="_blank" sx={{ mb: 1 }}>
                View Material
              </Button>
            )}

            {/* Complete button */}
            {!isDone && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    const fd = task.task_type === 'form' && Object.keys(formValues).length > 0 ? formValues : undefined;
                    onComplete(task.id, fd);
                  }}>
                  Mark Complete
                </Button>
              </Box>
            )}

            {/* Show completion info */}
            {isDone && task.completed_at && (
              <Typography variant="caption" color="text.secondary">
                Completed {formatDate(task.completed_at)}
                {task.status === 'skipped' && ' (Skipped)'}
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Card>
  );
}

export default function MyOnboardingSection() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const { data: instance, isLoading } = useQuery({
    queryKey: ['my-onboarding'],
    queryFn: () => onboardingApi.getMyOnboarding(),
    select: (res) => res.data,
  });

  const completeMutation = useMutation({
    mutationFn: ({ taskId, formData }: { taskId: string; formData?: Record<string, unknown> }) =>
      onboardingApi.completeTask(taskId, { form_data: formData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-onboarding'] });
      showSuccess('Task completed!');
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed to complete task'),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      onboardingApi.uploadDocument(taskId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-onboarding'] });
      showSuccess('Document uploaded!');
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Upload failed'),
  });

  if (isLoading) return <LinearProgress />;
  if (!instance) return null; // No active onboarding

  const tasksByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat.key] = (instance.tasks || []).filter(t => t.category === cat.key);
    return acc;
  }, {} as Record<string, OnboardingTask[]>);

  // Filter to only categories that have tasks (avoids Stepper index desync)
  const activeCategories = CATEGORIES.filter(cat => tasksByCategory[cat.key].length > 0);

  // Find the active step among rendered categories
  const activeStep = activeCategories.findIndex(cat => {
    const tasks = tasksByCategory[cat.key];
    return tasks.some(t => t.status !== 'completed' && t.status !== 'skipped');
  });

  const progressColor = instance.progress_pct >= 80 ? 'success' : instance.progress_pct >= 50 ? 'warning' : 'error';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={TYPE.bold}>My Onboarding</Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            <Box sx={{ minWidth: 160 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">{instance.completed_tasks}/{instance.total_tasks} tasks</Typography>
                <Typography variant="caption" fontWeight={TYPE.bold}>{instance.progress_pct}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={instance.progress_pct} color={progressColor}
                sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          </Stack>
        </Stack>

        {instance.status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Onboarding complete! Welcome to the team.
          </Alert>
        )}

        <Stepper activeStep={activeStep >= 0 ? activeStep : activeCategories.length} orientation="vertical">
          {activeCategories.map((cat) => {
            const tasks = tasksByCategory[cat.key];
            const completed = tasks.filter(t => t.status === 'completed' || t.status === 'skipped').length;
            const allDone = completed === tasks.length;

            return (
              <Step key={cat.key} completed={allDone} expanded>
                <StepLabel
                  optional={
                    <Typography variant="caption" color="text.secondary">{cat.description}</Typography>
                  }
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontWeight={TYPE.semibold}>{cat.label}</Typography>
                    <Chip label={`${completed}/${tasks.length}`} size="small"
                      color={allDone ? 'success' : 'default'} variant="outlined" />
                  </Stack>
                </StepLabel>
                <StepContent>
                  {tasks.map(task => (
                    <TaskCard key={task.id} task={task}
                      onComplete={(taskId, formData) => completeMutation.mutate({ taskId, formData })}
                      onUpload={(taskId, file) => uploadMutation.mutate({ taskId, file })} />
                  ))}
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </CardContent>
    </Card>
  );
}
